/** Team roles (GET /api/agent/roles) and one role's brief (GET /api/agent/roles/{id} → docs/agents/<role>.md). */
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api, unwrap } from "../../api/client";
import { qk } from "../../api/keys";
import { PageHeader } from "../../components/PageHeader";
import { Markdown } from "../../lib/markdown";
import { ClientMdButtons, QueryView, Section, TableScroll } from "../lib/ui";

const kindLabel = (k: string) => (k === "human" ? "person" : "bot");

export function RolesSection() {
  const roles = useQuery({ queryKey: qk.list("roles"), queryFn: () => unwrap(api.GET("/api/agent/roles")), staleTime: 300_000 });
  return (
    <Section id="roles" title="Roles">
      <QueryView q={roles} what="roles">
        {(rs) => (
          <TableScroll label="Role table">
            <table className="ws7-table" aria-label="Roles" data-testid="roles-table">
              <thead>
                <tr>
                  <th scope="col">Role</th><th scope="col">Summary</th><th scope="col">Scopes</th><th scope="col">Start page</th>
                  <th scope="col">Work queue</th><th scope="col">Usually</th>
                </tr>
              </thead>
              <tbody>
                {rs.map((r) => (
                  <tr key={r.id} data-testid={`role-row-${r.id}`}>
                    <th scope="row"><Link to={`/agent/${r.id}`} data-testid={`role-link-${r.id}`}>{r.title}</Link></th>
                    <td>{r.summary}</td>
                    <td>{r.scopes.join(", ")}</td>
                    <td><Link to={r.home}>{r.home}</Link></td>
                    <td>{r.queue ?? "—"}</td>
                    <td>{kindLabel(r.kind_default)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroll>
        )}
      </QueryView>
    </Section>
  );
}

export function RoleBriefView({ roleId }: { roleId: string }) {
  const brief = useQuery({
    queryKey: qk.list("role-brief", { roleId }),
    queryFn: () => unwrap(api.GET("/api/agent/roles/{role_id}", { params: { path: { role_id: roleId } } })),
    retry: false,
  });
  const r = brief.data?.role;
  return (
    <div className="ws7-page" data-testid="role-brief-page">
      <PageHeader title={r ? `${r.title} brief` : "Role brief"}>
        <ClientMdButtons filename={`role-${roleId}.md`} markdown={brief.data?.markdown ?? null} />
      </PageHeader>
      <p><Link to="/agent" data-testid="role-back">All roles and the agent guide</Link></p>
      <QueryView q={brief} what="the role brief">
        {(b) => (
          <>
            <dl className="ws7-kv" data-testid="role-meta">
              <dt>Role</dt><dd>{b.role.title} (<code>{b.role.id}</code>)</dd>
              <dt>Summary</dt><dd>{b.role.summary}</dd>
              <dt>Scopes</dt><dd>{b.role.scopes.join(", ")}</dd>
              <dt>Start page</dt><dd><Link to={b.role.home} data-testid="role-home">{b.role.home}</Link></dd>
              <dt>Work queue</dt><dd>{b.role.queue ? <code>GET /api/work/next?queue={b.role.queue}</code> : "—"}</dd>
              <dt>Usually</dt><dd>{kindLabel(b.role.kind_default)}</dd>
            </dl>
            <Section id="brief" title="Brief">
              <Markdown source={b.markdown} testId="role-brief" />
            </Section>
          </>
        )}
      </QueryView>
    </div>
  );
}
